import { useState, useMemo, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Star,
  Eye,
  Shield,
  Gem,
} from "lucide-react";

const CompanyValuesSlider = ({ announcement = {}, customColors = {} }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Define the base company values from props
  const companyValues = useMemo(
    () => [
      {
        id: "vision",
        title: "Vision",
        content: announcement?.vision || "No vision statement provided.",
        Icon: Star,
        color: customColors.highlight || "#A78BFA",
      },
      {
        id: "mission",
        title: "Mission",
        content: announcement?.mission || "No mission statement provided.",
        Icon: Eye,
        color: customColors.active || "#60A5FA",
      },
      {
        id: "goals",
        title: "Goals",
        content: announcement?.goals || "No goals defined.",
        Icon: Shield,
        color: customColors.accent || "#F472B6",
      },
      {
        id: "coreValues",
        title: "Core Values",
        content:
          announcement?.coreValues || "No core values have been provided.",
        Icon: Gem,
        color: customColors.success || "#4ADE80",
      },
    ],
    [announcement, customColors]
  );

  // Process the values to split long content into multiple slides
  const slides = useMemo(() => {
    const MAX_CHARS_PER_SLIDE = 150; // Adjust this value as needed
    const allSlides = [];

    companyValues.forEach((value) => {
      if (!value.content) return;
      // If content is short, add it as a single slide
      if (value.content.length <= MAX_CHARS_PER_SLIDE) {
        allSlides.push(value);
      } else {
        // If content is long, split it by words into manageable chunks
        const words = value.content.split(" ");
        let currentChunk = "";
        const chunks = [];

        for (const word of words) {
          if ((currentChunk + " " + word).length > MAX_CHARS_PER_SLIDE) {
            chunks.push(currentChunk.trim());
            currentChunk = word;
          } else {
            currentChunk += (currentChunk ? " " : "") + word;
          }
        }
        // Add the last remaining chunk
        if (currentChunk) {
          chunks.push(currentChunk.trim());
        }

        // Create a new slide for each chunk with an updated title
        chunks.forEach((chunk, index) => {
          allSlides.push({
            ...value,
            title: `${value.title} (${index + 1}/${chunks.length})`,
            content: chunk,
          });
        });
      }
    });

    return allSlides;
  }, [companyValues]);

  // Effect for auto-playing the slider
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const goPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  if (slides.length === 0) {
    return (
      <div className="relative h-[180px] font-sans flex items-center justify-center rounded-xl bg-white border border-gray-100">
        <p className="text-gray-500">No content available.</p>
      </div>
    );
  }

  const activeSlide = slides[currentIndex];

  return (
    <div className="relative h-[180px] font-sans">
      <div className="rounded-xl p-4 relative overflow-hidden shadow-sm border border-gray-100 bg-white h-full flex flex-col justify-center">
        {/* We use the slide content and index as a key to trigger the animation on change */}
        <div
          key={activeSlide.content + currentIndex}
          className="animate-fade-in text-center"
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm mx-auto mb-2"
            style={{
              backgroundColor: activeSlide.color + "1A", // Adds opacity to the color
            }}
          >
            <activeSlide.Icon
              className="w-5 h-5"
              style={{ color: activeSlide.color }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h4
              className="text-base font-bold uppercase tracking-wider mb-1"
              style={{ color: customColors.textPrimary || "#1e293b" }}
            >
              {activeSlide.title}
            </h4>
            <p
              className="text-sm leading-snug font-medium text-balance px-4"
              style={{ color: customColors.textSecondary || "#64748b" }}
            >
              {activeSlide.content}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation buttons are only shown if there is more than one slide */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute top-1/2 left-2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200 ease-in-out"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            aria-label="Next slide"
            className="absolute top-1/2 right-2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200 ease-in-out"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
};

export default CompanyValuesSlider;
